import type { AxiosError } from 'axios';
import axios from 'axios';
import type { ActorRefFrom } from 'xstate';
import { assign, fromPromise, not, setup, stateIn } from 'xstate';
import { ApolloError } from '@apollo/client';
import { partMachine } from './PartUploaderMachine';
import type {
  AssetUrlLookupFn,
  PartForStateMachine as Part,
  UploaderOptions,
} from './types';
import type Store from './UploaderStore';

const MAX_CONCURRENCY = 4;
const CHILD_MACHINE_ID_PREFIX = 'part-machine-';

// Default values for configurable thresholds
const DEFAULT_PENDING_PARTS_THRESHOLD = 10;
const DEFAULT_PART_REQUEST_COUNT = 20;

const MAX_URL_FETCH_RETRY_ATTEMPTS = 30;

type MachineContext = {
  maxConcurrency: number;
  pendingPartsThreshold: number;
  partRequestCount: number;
  subMachines: ActorRefFrom<typeof partMachine>[];
  store: Store;
  error?: AxiosError | ApolloError;
  assetUrlLookupFn?: AssetUrlLookupFn;
  callbacks?: Omit<UploaderOptions['callbacks'], 'onProgress'>;
  isLoggingEnabled?: boolean;
  urlFetchRetryAttempt: number;
  urlFetchRetryMaxAttempts: number;
};

export const uploaderMachine = setup({
  types: {
    context: {} as MachineContext,
    input: {} as {
      store: Store;
      maxConcurrency?: number;
      pendingPartsThreshold?: number;
      partRequestCount?: number;
      assetUrlLookupFn?: AssetUrlLookupFn;
      callbacks?: Omit<UploaderOptions['callbacks'], 'onProgress'>;
      isLoggingEnabled?: boolean;
      urlFetchRetryMaxAttempts?: number;
    },
  },
  guards: {
    areAllPartMachinesDone: ({ context }) =>
      context.subMachines.every(
        (machine) => machine.getSnapshot().status === 'done'
      ),
    hasMoreURLsToFetch: ({ context }) => {
      const nextParts = context.store.getNextAdjacentPartsToFetch(1).length > 0;
      const pendingBelowThreshold =
        context.store.getProgress().pending < context.pendingPartsThreshold;
      const notAllCompleted =
        context.store.getProgress().completed < context.store.totalPartCount;

      if (context.isLoggingEnabled) {
        // eslint-disable-next-line no-console
        console.log('[Uploader] hasMoreURLsToFetch check:', {
          nextParts,
          pendingBelowThreshold,
          notAllCompleted,
          pending: context.store.getProgress().pending,
          completed: context.store.getProgress().completed,
          total: context.store.totalPartCount,
        });
      }

      return nextParts && pendingBelowThreshold && notAllCompleted;
    },
    isNetworkError: ({ event }) => {
      const error = event.error as ApolloError;
      return error.networkError != null;
    },
    needsInitialUrlFetch: ({ context }) => {
      // Check if all parts have no URL
      const allParts = [...context.store.getState().allParts];
      return allParts.every((part) => !part.url);
    },
    hasPartsToUpload: ({ context }) => {
      return context.store.totalPartCount > 0;
    },
    isPartUploaderRunning: stateIn({
      RUNNING: {
        UPLOADING_PARTS: 'RUNNING',
      },
    }),
  },
  actors: {
    addPartsToStore: fromPromise(
      ({ input }: { input: { parts: Part[]; store: Store } }) => {
        return new Promise<void>((resolve) => {
          // get the next part from the store
          input.store.setParts(input.parts);
          resolve();
        });
      }
    ),

    fetchUrls: fromPromise(
      async ({
        input,
      }: {
        input: {
          store: Store;
          assetUrlLookupFn?: AssetUrlLookupFn;
          partRequestCount: number;
        };
      }) => {
        const nextPartsToFetch = input.store.getNextAdjacentPartsToFetch(
          input.partRequestCount
        );

        if (nextPartsToFetch.length === 0) {
          return Promise.resolve();
        }

        // If there are parts to fetch, fetch the urls
        if (input.assetUrlLookupFn) {
          return input
            .assetUrlLookupFn(
              nextPartsToFetch[0].index,
              nextPartsToFetch.length
            )
            .then((urls) => {
              // update the parts with the urls
              input.store.setPartUrls(nextPartsToFetch, urls);
            });
        }
        return Promise.reject(
          new ApolloError({
            errorMessage: 'no asset url lookup function',
            networkError: null,
          })
        );
      }
    ),
    partMachine,
  },
  actions: {
    initSampling: ({ context }) => {
      context.store.initSampling();
    },
    notifyUrlsUpdated: ({ context }) => {
      // Notify all part machines that URLs have been updated
      context.subMachines.forEach((machine) => {
        machine.send({ type: 'URL_UPDATED' });
      });
    },
    spawnPartMachines: assign({
      subMachines: ({ spawn, self, context }) => {
        if (context.subMachines.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            `spawnPartMachines: ${context.subMachines.length} subMachines already exists`
          );
        }
        return Array(
          Math.min(context.maxConcurrency, context.store.getProgress().pending)
        )
          .fill(0)
          .map((v, index) => {
            return spawn('partMachine', {
              id: `${CHILD_MACHINE_ID_PREFIX}${index}`,
              input: {
                parentRef: self,
                store: context.store,
                isLoggingEnabled: context.isLoggingEnabled,
              },
            });
          });
      },
    }),
    killAllPartMachines: assign({
      subMachines: ({ context }) => {
        context.subMachines.forEach((subMachine) => {
          if (subMachine.getSnapshot().status !== 'done') {
            subMachine.send({ type: 'STOP' });
          }
        });
        return [];
      },
    }),

    abortPendingParts: ({ context }) => {
      context.store.abortPendingParts();
    },

    callOnStart: ({ context }) => {
      context.callbacks?.onStart?.();
    },

    callOnSuccess: ({ context }) => {
      context.callbacks?.onSuccess?.();
    },

    callOnError: ({ context }) => {
      context.callbacks?.onError?.(context.error as AxiosError);
    },
    log: ({ context }, params: { message: string }) => {
      if (context.isLoggingEnabled) {
        // eslint-disable-next-line no-console
        console.log('[Uploader]', params.message);
      }
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFcAOAbA9gQwgWgCtMAjAOgGEAlAUQEEAVagEQGJammB9ABVsvoDKAbQAMAXUShUmWAEsALrMwA7SSAAeiAIwA2AKykALACZDATgsB2YyL3WtlgDQgAnttuk9OgByWztvRFjYz0tMwBfcOc0LFxCElJ2JgBJADkAcR4+QRYIFTBSWWUAN0wAawKYnHwiMiS0zN5+AQQi0oBjbEUVUTFetWk5btUkDUQAZnHLUl0-MxszHUtDQ289ZzcEYx1DUhsTPUm-QPG9SOiMavi6jgas5tz8wpLyysu42sTbjPvBVpfOsNekItBJRoMFEoRqBNAhxsZxqQRIstKjvDoRFN0YYNohDFoRJ4zJYlpZkUtUVozlEQFUPglKABVVKpBqPZQFWDyLpvWI1BnM1kZfrgmSQlRqWFmbyI-R6GxacZmLSrSzrVyIbzoozK8YiNbow5abznWnvflkJkshqkRmUAAynHIAAlqOQANLUSikZJMe3UFhNeicRnce0AeXYTvDAFkw9RGCKpGLhpK8WYDIdDGSLAT4d5dLitlSdKRlvpkTZAotTXSLaQrUL0raHU7XR6vT6-QHuJRw+RqAIBJwmOHUtQkyAIanRrDzJnxtnkcrMcYCzoi8YCdMtYYRKs7KZvIZa+brg3BTa7Y6XW7Pd6AGIJl13a8CdkFNqvUh18+Nq+treHaPs+zqvg6LRfoCULAuIAwplCaYIMShLLASIi6GExjWOqmxbjoZh7NhvjGkEKoRDSv6fP+GQtje7b3qQT70C+Pxvh+zylBUP5ntRl60debZ3p2zGsZkb7-B0XQweIIJgsmQyIbO6YLkuuaruum4iDoxhInu2xBGShh2KefJ-vxzaCUBjGiWBbEQSwYAAE5OZgTmkBgXQAGZuQAtjxZl8daAmAQxImgeB9qQQC0k9LJcGiopErKchZJlviIgYToWE4Vp5hIseQShLq-jUhcgUCsFllhpGKQ-EGAgXlVHFcjyAVXEFTa2jV7B3A1TVNpO05KTC2i6IRNhymsdg7IqRa6HohGGDs1inLYWgIielG8ZVXWhhGvX1dkjU0ekLAPrQyT+kwQ0Iclo0IGESqkPM4w6EsCL+N4IhOBqj1btMa76P4GEOCEWimR1u1Xj1dWNMdA1shdV3MHJ8FJdCYzFpYiJhJYxo44uxLGEWegrHsBYZmSazmNskP0paFndQdcO-CdFmBrQjICBOCUKeKmOwhtyJ7Npeh6NNJL4uM83iwYMokmEhynBm9P1qdzO1X1COnSw5C0KkA72rdGNIboP3pd4xgZtlGKGDLf0YoSVMYfqBYyocavmVVmuHfDzSIwJsNsr2-aDsOo7jibAtIcthI+JYyzzGEP2Fn9C27LY30ynqGIhOMXudTDLPawHGsCPQDDc0JHosNHM4PQWuzGlS4xhDbmLeLL1ieL4CK+GTxMmttFWMz7+1a0dZdMxXVfDtZdegujMcpasWikOMvgkgW2wSySss7J4+o-Ts2botKhfQ0HJdT4IpC8NzzAsDQAiMjGvPyVOd2C2NFii1N4spZzXToEUsxJQgSxBtKAuI8oZjz2sHW+jUH481YPrQ21BjZ8y-qbFKKp4QbxMPYWma8cR-RxktBEANTCYWwpEGkyhMAQDgGoKiJBl4NyxngDcf08AGAsBYYyW5FymAxDA8qcCKA0AYMwDhI0sYmHmh4bYpgVH22tn4CGsCGZfFZg1OR90sZvQMDseYqx7Yyl0GQzYBZSBLHMAaNYIR5gUQkTo06Bif6PQInYnSyxlr42JCsXC2gbC6UmG3HSWJkQ-UvvAgC9FhKUE8UhNciJFqBO3KnMI80zDk1OFqYGOh4QJziYHSyoUkldn9CklKkx14ZLCFk-GOS-o2FseYII9t-BqmNDoMpGsrJhRAixOy4kIK1IepYGUMx-DSlRPKeYfgtIYk8OYd6a0NlbTcerJmQyqnI2upMrGG0bB7EOFmbeJJMRaQIZiJUfhjzoiWOIs0o9yl0RroxSO1BjlC2CISfOlyE5iM3LoUsC14TWymA4gZezEH+0EH8xAANfHYRWBSIJxl5rW0JJlb6C1sIETMK8th8Tr6T0RezKqyKtj6DRf4zF-iQmPSmNMHwepTB2DJhLfp2jdnjwRWzD5E8-a0rbrKPxGLAnMtlv4GYphAlWxlCYYwcLBU3ypR82e9Bq7WVpTsXS4sgjErsNYe2crdI6WzOLFRaEypvMkYMoV-UNbUFSEwWlBJFQKvlOfZ5BEHabCpMqDeidSrZipMedVCDNXCpQbIxKK8Hoqi3EYY0Td5jZX1EG7Q2ZCIqmRCYBwlM1QxuLpS4VoqmDz1jPGRgXq1y6S3BG48a4HC5u8QQpuOM1wKxPmUw5ib+acKFq9F6JLsL7nucrIs6JETgOKQWImar+XnnQUbYdODk0nIyS9bSiwca2DFl3P6nKJ3rUVIZLNZTyB1v9A2pNo7EDWEJBtRYJKNpPR+rk0sbdiSCJ0uSTE9DwhAA */
  id: 'upload-job',

  context: ({ input }) => ({
    maxConcurrency: input.maxConcurrency ?? MAX_CONCURRENCY,
    pendingPartsThreshold:
      input.pendingPartsThreshold ?? DEFAULT_PENDING_PARTS_THRESHOLD,
    partRequestCount: input.partRequestCount ?? DEFAULT_PART_REQUEST_COUNT,
    urlFetchRetryAttempt: 0,
    urlFetchRetryMaxAttempts:
      input.urlFetchRetryMaxAttempts ?? MAX_URL_FETCH_RETRY_ATTEMPTS,
    subMachines: [],
    store: input.store,
    error: undefined,
    assetUrlLookupFn: input.assetUrlLookupFn,
    callbacks: input.callbacks,
    isLoggingEnabled: input.isLoggingEnabled ?? false,
  }),

  initial: 'CREATED',
  states: {
    CREATED: {
      on: {
        ADD_PARTS: {
          target: 'ADDING_PARTS',
        },
      },
    },
    ADDING_PARTS: {
      invoke: {
        src: 'addPartsToStore',
        input: ({ event, context }) => {
          return { parts: event.parts, store: context.store };
        },
        onDone: [
          {
            guard: 'hasPartsToUpload',
            target: 'RUNNING',
          },
          {
            target: 'COMPLETE',
          },
        ],
      },
    },
    RUNNING: {
      type: 'parallel',
      entry: ['callOnStart'],
      states: {
        URL_CHECKER: {
          initial: 'CHECK_INITIAL_STATE',
          states: {
            CHECK_INITIAL_STATE: {
              always: [
                {
                  guard: 'needsInitialUrlFetch',
                  target: 'FETCHING_URLS',
                  actions: {
                    type: 'log',
                    params: {
                      message: 'Initial URL fetch needed',
                    },
                  },
                },
                {
                  target: [
                    'IDLE',
                    '#upload-job.RUNNING.UPLOADING_PARTS.RUNNING',
                  ],
                  actions: {
                    type: 'log',
                    params: {
                      message: 'No initial URL fetch needed',
                    },
                  },
                },
              ],
            },
            IDLE: {
              entry: [
                {
                  type: 'log',
                  params: {
                    message: 'URL checker idle',
                  },
                },
              ],
              on: {
                // We have to duplicate the logic because Xstate doesn't support
                // multiple events for the same transition
                PART_UPLOAD_COMPLETE: [
                  {
                    guard: 'hasMoreURLsToFetch',
                    target: 'FETCHING_URLS',
                    actions: {
                      type: 'log',
                      params: {
                        message: 'Fetching more URLs after part completion',
                      },
                    },
                  },
                ],
                FETCH_NEW_URLS: [
                  {
                    guard: 'hasMoreURLsToFetch',
                    target: 'FETCHING_URLS',
                    actions: {
                      type: 'log',
                      params: {
                        message: 'Fetching new URLs requested',
                      },
                    },
                  },
                ],
              },
            },
            FETCHING_URLS: {
              entry: [
                {
                  type: 'log',
                  params: {
                    message: 'Starting URL fetch',
                  },
                },
              ],
              invoke: {
                src: 'fetchUrls',
                input: ({
                  context: { store, assetUrlLookupFn, partRequestCount },
                }) => ({
                  store,
                  assetUrlLookupFn,
                  partRequestCount,
                }),
                onDone: [
                  {
                    guard: not('isPartUploaderRunning'),
                    target: [
                      'IDLE',
                      '#upload-job.RUNNING.UPLOADING_PARTS.RUNNING',
                    ],
                    actions: ['notifyUrlsUpdated'],
                  },
                  {
                    target: ['IDLE'],
                    actions: ['notifyUrlsUpdated'],
                  },
                ],
                onError: [
                  {
                    guard: 'isNetworkError',
                    target: 'RETRY_FETCH',
                    actions: [
                      {
                        type: 'log',
                        params: {
                          message: 'Network error during URL fetch, will retry',
                        },
                      },
                      assign({
                        urlFetchRetryAttempt: ({ context }) =>
                          context.urlFetchRetryAttempt + 1,
                      }),
                    ],
                  },
                  {
                    target: '#upload-job.FAILED',
                    actions: [
                      {
                        type: 'log',
                        params: {
                          message:
                            'Non-network error during URL fetch, failing',
                        },
                      },
                      assign({
                        error: ({ event }) => event.error as ApolloError,
                      }),
                    ],
                  },
                ],
              },
            },
            RETRY_FETCH: {
              after: {
                2000: [
                  {
                    target: 'FETCHING_URLS',
                    guard: ({ context }) =>
                      context.urlFetchRetryAttempt <
                      context.urlFetchRetryMaxAttempts,
                  },
                  {
                    target: '#upload-job.FAILED',
                    actions: [
                      {
                        type: 'log',
                        params: {
                          message: 'Max URL fetch retries reached',
                        },
                      },
                      assign({
                        error: () => {
                          return new ApolloError({
                            errorMessage: 'Max URL fetch retries reached',
                          });
                        },
                      }),
                    ],
                  },
                ],
              },
            },
          },
        },
        UPLOADING_PARTS: {
          initial: 'IDLE',
          states: {
            IDLE: {},
            RUNNING: {
              initial: 'UPLOADING',
              entry: [
                'initSampling',
                'killAllPartMachines',
                'spawnPartMachines',
                {
                  type: 'log',
                  params: {
                    message: 'Entering running state',
                  },
                },
              ],
              states: {
                UPLOADING: {
                  on: {
                    PROCESS_DONE: {
                      target: 'STATUS_CHECK',
                    },
                  },
                },
                STATUS_CHECK: {
                  always: [
                    {
                      guard: 'areAllPartMachinesDone',
                      actions: {
                        type: 'log',
                        params: {
                          message: 'All parts completed',
                        },
                      },
                      target: 'END',
                    },
                    {
                      actions: {
                        type: 'log',
                        params: {
                          message: 'Parts still in progress',
                        },
                      },
                      target: 'UPLOADING',
                    },
                  ],
                },
                END: {
                  type: 'final',
                },
              },
              on: {
                FAILED: [
                  {
                    target: '#upload-job.CANCELED',
                    actions: [
                      {
                        type: 'log',
                        params: {
                          message: 'Upload canceled',
                        },
                      },
                    ],
                    guard: ({ event }) => axios.isCancel(event.error),
                  },
                  {
                    target: '#upload-job.FAILED',
                    actions: [
                      {
                        type: 'log',
                        params: {
                          message: 'Failed processing assets',
                        },
                      },
                      assign({ error: ({ event }) => event.error }),
                    ],
                  },
                ],
                PAUSE: 'PAUSED',
                CANCEL: '#upload-job.CANCELED',
              },
              onDone: {
                target: '#upload-job.COMPLETE',
              },
            },
            PAUSED: {
              entry: ['killAllPartMachines', 'abortPendingParts'],
              on: {
                RESUME: 'RUNNING',
                CANCEL: '#upload-job.CANCELED',
              },
            },
          },
        },
      },
    },
    FAILED: {
      entry: [
        {
          type: 'log',
          params: {
            message: 'Upload failed',
          },
        },
        'killAllPartMachines',
        'abortPendingParts',
        'callOnError',
      ],
      type: 'final',
    },
    CANCELED: {
      entry: [
        {
          type: 'log',
          params: {
            message: 'Upload canceled',
          },
        },
        'killAllPartMachines',
        'abortPendingParts',
      ],
      type: 'final',
    },
    COMPLETE: {
      entry: [
        'callOnSuccess',
        {
          type: 'log',
          params: {
            message: 'Upload complete',
          },
        },
        'killAllPartMachines',
      ],
      type: 'final',
    },
  },
});
